import { Check, Upload } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import {
  Button,
  ChipToggle,
  Field,
  Input,
  Select,
  useToast,
} from '@/components/ui';
import {
  useConsultationOptions,
  useEncounterActions,
  type Encounter,
} from '@/features/encounters/api';
import {
  VACCINE_BATCH_MAX,
  vaccineBatchError,
  type VaccinationRequest,
} from '@/features/encounters/vaccination';
import { useSession } from '@/features/auth/session-context';
import { useChildren } from '@/features/mch/api';
import { isApiError } from '@/lib/api';
import { clinicDate } from '@/lib/clinic-time';
import { klTime } from '@/pages/calendar/time';
import { useLivePrescription, type FormKind } from './consult-utils';
import styles from './consult.module.css';

/** RL-1862 (gap #140): time slip times are 24-hour, like the printed slip. */
const HHMM = /^([01]\d|2[0-3]):[0-5]\d$/;
const BAD_TIME = 'Use the 24-hour time, like 14:30.';

const PHQ9 = [
  'Little interest or pleasure in doing things',
  'Feeling down, depressed or hopeless',
  'Trouble falling or staying asleep, or sleeping too much',
  'Feeling tired or having little energy',
  'Poor appetite or overeating',
  'Feeling bad about yourself, or that you are a failure',
  'Trouble concentrating on things',
  'Moving or speaking slowly, or being fidgety or restless',
  'Thoughts that you would be better off dead, or of hurting yourself',
];
const PHQ_ANSWERS = [
  'Not at all',
  'Several days',
  'More than half the days',
  'Nearly every day',
];
const STEP_LABEL: Record<string, string> = {
  adrenaline_given: 'Adrenaline given',
  called_999: '999 called',
  cpr_started: 'CPR started',
  aed_attached: 'AED attached',
  oxygen_started: 'Oxygen started',
  ambulance_arrived: 'Ambulance arrived',
};

/** Whole years from a YYYY-MM-DD birth date to a YYYY-MM-DD clinic date. */
function ageInYears(dob: string, today: string): number {
  const years = Number(today.slice(0, 4)) - Number(dob.slice(0, 4));
  return today.slice(5) < dob.slice(5) ? years - 1 : years;
}

/**
 * One "More for this visit" form, shown in the encounter drawer in place of
 * the consultation (Admin v2 #73–#82), with "Back to consultation".
 */
export function VisitForm({
  kind,
  encounter: e,
  onBack,
}: {
  kind: FormKind;
  encounter: Encounter;
  onBack: () => void;
}) {
  const a = useEncounterActions(e.id);
  const { data: opts } = useConsultationOptions();
  const { hasPermission } = useSession();
  // RL-1853: a child's schedule (for the next due dose) from Mothers & children.
  const dob = e.patient.dateOfBirth;
  const children = useChildren(
    kind === 'vaccination' &&
      Boolean(dob) &&
      hasPermission('mothers_children:read'),
  );
  const live = useLivePrescription(e.id);
  const toast = useToast();
  const [v, setV] = useState<Record<string, string>>({});
  const [multi, setMulti] = useState<string[]>([]);
  const [phq, setPhq] = useState<number[]>(Array(9).fill(0));
  const [file, setFile] = useState<File | null>(null);
  /** Field messages from the last refused save (400 VALIDATION_FAILED). */
  const [apiField, setApiField] = useState<Record<string, string>>({});
  const set = (k: string) => (x: string) => {
    setV((s) => ({ ...s, [k]: x }));
    setApiField(({ [k]: _drop, ...rest }) => rest);
  };
  const val = (k: string, d = '') => v[k] ?? d;

  const fail = (err: unknown) => {
    setApiField(
      isApiError(err)
        ? Object.fromEntries(err.fieldErrors.map((f) => [f.field, f.message]))
        : {},
    );
    toast.show({
      tone: 'error',
      title: isApiError(err)
        ? (err.problem?.title ?? 'Not saved')
        : 'Not saved',
      body: isApiError(err) ? err.message : undefined,
      error: err,
    });
  };
  const submit = (path: string, body: object, done: string) =>
    a.submit
      .mutateAsync({ path, body })
      .then(() => {
        toast.show({ tone: 'success', title: done });
        onBack();
      })
      .catch(fail);

  const chips = (
    label: string,
    k: string,
    options: [string, string][],
    d: string,
  ) => (
    <div className={styles.section}>
      <p className="eyebrow">{label}</p>
      <ChipToggle
        label={label}
        single
        options={options.map(([value, l]) => ({ value, label: l }))}
        selected={[val(k, d)]}
        onChange={([x]) => set(k)(x)}
      />
    </div>
  );

  let body: ReactNode = null;
  let action: { label: string; run: () => void; disabled?: boolean } | null =
    null;

  switch (kind) {
    case 'in-clinic-test': {
      const test = val('test', opts?.inClinicTests[0] ?? '');
      body = (
        <>
          {chips(
            'Test',
            'test',
            (opts?.inClinicTests ?? []).map((t) => [t, t]),
            opts?.inClinicTests[0] ?? '',
          )}
          <Field label="Result">
            <Input
              value={val('result')}
              placeholder="e.g. 7.8 mmol/L, 6.4 %, protein 1+, negative"
              onChange={(x) => set('result')(x.target.value)}
            />
          </Field>
          {test === 'Capillary glucose' &&
            chips(
              'Fasting or random (glucose)',
              'ctx',
              [
                ['random', 'Random'],
                ['fasting', 'Fasting'],
              ],
              'random',
            )}
          <p className={styles.sub}>
            Recorded as a result on the record straight away.
          </p>
        </>
      );
      action = {
        label: 'Save result',
        disabled: !val('result').trim(),
        run: () =>
          void submit(
            'in-clinic-tests',
            {
              test,
              result: val('result').trim(),
              ...(test === 'Capillary glucose'
                ? { glucoseContext: val('ctx', 'random') }
                : {}),
            },
            'Result added to the record',
          ),
      };
      break;
    }
    case 'vaccination': {
      const batch = val('batchNumber').trim();
      const batchError = vaccineBatchError(val('batchNumber'));
      // RL-1853 (decision D15): under the Mothers & children age limit (the
      // same limit the API checks) the childhood schedule comes first, with
      // the next due dose chosen.
      const maxAge = children.data?.config.children.maxAgeYears;
      const isChild =
        !!dob && maxAge !== undefined && ageInYears(dob, clinicDate()) < maxAge;
      const childList = isChild ? (opts?.childVaccines ?? []) : [];
      const schedule = children.data?.rows.find(
        (r) => r.patient.id === e.patient.id,
      )?.schedule;
      const nextDue = schedule?.find(
        (s) => !s.given && !s.optional && childList.includes(s.vaccine),
      )?.vaccine;
      const defaultVaccine = nextDue ?? childList[0] ?? opts?.vaccines[0] ?? '';
      // Batch 8 review: no choice until the child's schedule is in, so the
      // preselected vaccine does not change under the doctor's click.
      const scheduleLoading = children.isLoading;
      const vaccine = val('vaccine', scheduleLoading ? '' : defaultVaccine);
      const childDose = childList.includes(vaccine);
      body = (
        <>
          <Field
            label="Vaccine"
            hint={
              childDose
                ? "Childhood schedule: also recorded on the child's schedule in Mothers & children."
                : undefined
            }
          >
            <Select
              value={vaccine}
              onChange={(x) => set('vaccine')(x.target.value)}
              options={[
                ...childList.map((x) => ({
                  value: x,
                  label:
                    x === nextDue
                      ? `${x} · childhood schedule · due next`
                      : `${x} · childhood schedule`,
                })),
                ...(opts?.vaccines ?? []).map((x) => ({
                  value: x,
                  label: x,
                })),
              ]}
            />
          </Field>
          {chips(
            'Dose',
            'dose',
            [
              ['1', '1'],
              ['2', '2'],
              ['3', '3'],
              ['booster', 'Booster'],
            ],
            '1',
          )}
          {chips(
            'Site',
            'site',
            [
              ['left_deltoid', 'Left deltoid'],
              ['right_deltoid', 'Right deltoid'],
              ['left_thigh', 'Left thigh'],
              ['right_thigh', 'Right thigh'],
            ],
            'left_deltoid',
          )}
          <Field label="Arrival date in Saudi Arabia (Umrah and Hajj) · optional">
            <Input
              type="date"
              value={val('arrival')}
              onChange={(x) => set('arrival')(x.target.value)}
            />
          </Field>
          <Field
            label="Batch number · optional"
            hint={`From the vial or pack, up to ${VACCINE_BATCH_MAX} characters. Printed on the certificate.`}
            error={batchError ?? apiField.batchNumber}
          >
            <Input
              value={val('batchNumber')}
              placeholder="e.g. FLU-26-0042"
              autoComplete="off"
              spellCheck={false}
              onChange={(x) => set('batchNumber')(x.target.value)}
            />
          </Field>
          {chips(
            'Previous reaction to a vaccine',
            'prev',
            [
              ['no', 'No'],
              ['yes', 'Yes'],
            ],
            'no',
          )}
          <p className={styles.blockNote}>
            After the injection the patient waits 15 minutes (30 after a
            previous reaction) before leaving. A certificate with a QR code is
            issued; conjugate MenACWY is valid 5 years for Hajj and Umrah. The
            certificate prints the batch number, or &ldquo;Not recorded&rdquo;
            when it is left blank.
          </p>
        </>
      );
      const request: VaccinationRequest = {
        vaccine,
        dose: val('dose', '1'),
        site: val('site', 'left_deltoid'),
        arrivalDate: val('arrival') || undefined,
        previousReaction: val('prev', 'no') === 'yes',
        batchNumber: batch || undefined,
      };
      action = {
        label: 'Record and issue certificate',
        disabled: Boolean(batchError) || scheduleLoading || !vaccine,
        run: () =>
          void submit(
            'vaccinations',
            request,
            'Vaccination recorded · certificate issued',
          ),
      };
      break;
    }
    case 'phq9': {
      const score = phq.reduce((x, y) => x + y, 0);
      body = (
        <>
          <p>
            Over the last 2 weeks, how often has the patient been bothered by:
          </p>
          {PHQ9.map((q, i) => (
            <div key={q} className={styles.section}>
              <p className="eyebrow">
                {i + 1}. {q}
              </p>
              <ChipToggle
                label={q}
                single
                options={PHQ_ANSWERS.map((x, n) => ({
                  value: String(n),
                  label: x,
                }))}
                selected={[String(phq[i])]}
                onChange={([x]) =>
                  setPhq((p) => p.map((y, j) => (j === i ? Number(x) : y)))
                }
              />
            </div>
          ))}
          <p className={styles.okNote}>Score {score} / 27</p>
          {phq[8] > 0 && (
            <p className={styles.blockNote} role="alert">
              Question 9 is positive: assess suicide risk now and follow the
              clinic’s crisis pathway. In immediate danger: call 999. Do not
              leave the patient alone.
            </p>
          )}
        </>
      );
      action = {
        label: 'Save score',
        run: () =>
          void submit('phq9', { answers: phq }, `PHQ-9 saved · score ${score}`),
      };
      break;
    }
    case 'imaging':
      body = (
        <>
          <Field label="Examination">
            <Select
              value={val('exam', opts?.imagingExams[0] ?? '')}
              onChange={(x) => set('exam')(x.target.value)}
              options={(opts?.imagingExams ?? []).map((x) => ({
                value: x,
                label: x,
              }))}
            />
          </Field>
          <Field label="Where">
            <Input
              value={val('where')}
              placeholder="Imaging centre or hospital"
              onChange={(x) => set('where')(x.target.value)}
            />
          </Field>
          <Field label="Clinical question">
            <Input
              value={val('question')}
              placeholder="e.g. rule out fracture"
              onChange={(x) => set('question')(x.target.value)}
            />
          </Field>
          <p className={styles.sub}>
            The report is attached when it comes back; the lab desk tracks it.
          </p>
        </>
      );
      action = {
        label: 'Send referral',
        disabled: val('where').trim().length < 2,
        run: () =>
          void submit(
            'imaging-referrals',
            {
              exam: val('exam', opts?.imagingExams[0] ?? ''),
              where: val('where').trim(),
              question: val('question').trim() || undefined,
            },
            'Imaging referral sent',
          ),
      };
      break;
    case 'time-slip': {
      const arrived = val(
        'arrived',
        e.queueTicket ? klTime(e.queueTicket.arrivedAt) : '09:00',
      );
      const leaving = val('leaving', klTime(new Date().toISOString()));
      body = (
        <>
          {chips(
            'For',
            'for',
            [
              ['patient', 'The patient'],
              ['companion', 'Person who came with the patient'],
            ],
            'patient',
          )}
          <Field label="Patient">
            <Input value={e.patient.fullName} disabled />
          </Field>
          <Field label="Companion name (if for the companion) · optional">
            <Input
              value={val('cname')}
              onChange={(x) => set('cname')(x.target.value)}
            />
          </Field>
          <Field label="Companion IC · optional">
            <Input
              value={val('cic')}
              onChange={(x) => set('cic')(x.target.value)}
            />
          </Field>
          <Field
            label="Arrived"
            hint="24-hour, as printed on the slip (e.g. 14:30)"
            error={HHMM.test(arrived) ? undefined : BAD_TIME}
          >
            <Input
              inputMode="numeric"
              maxLength={5}
              placeholder="HH:MM"
              value={arrived}
              aria-invalid={!HHMM.test(arrived)}
              onChange={(x) => set('arrived')(x.target.value)}
            />
          </Field>
          <Field
            label="Leaving"
            hint="24-hour, as printed on the slip (e.g. 14:30)"
            error={
              !HHMM.test(leaving)
                ? BAD_TIME
                : HHMM.test(arrived) && leaving <= arrived
                  ? 'Leaving must be after Arrived.'
                  : undefined
            }
          >
            <Input
              inputMode="numeric"
              maxLength={5}
              placeholder="HH:MM"
              value={leaving}
              aria-invalid={!HHMM.test(leaving)}
              onChange={(x) => set('leaving')(x.target.value)}
            />
          </Field>
          <p className={styles.sub}>
            A time slip shows attendance only. It is not sick leave; how
            employers treat it is up to their policy.
          </p>
        </>
      );
      action = {
        label: 'Issue time slip',
        disabled:
          !HHMM.test(arrived) || !HHMM.test(leaving) || leaving <= arrived,
        run: () =>
          void submit(
            'time-slips',
            {
              for: val('for', 'patient'),
              companionName: val('cname').trim() || undefined,
              companionIc: val('cic').trim() || undefined,
              arrived,
              leaving,
            },
            'Time slip issued',
          ),
      };
      break;
    }
    case 'attach':
      body = (
        <>
          <Field label="Photo, scanned letter or ECG strip">
            <input
              type="file"
              accept="image/jpeg,image/png,application/pdf"
              onChange={(x) => setFile(x.target.files?.[0] ?? null)}
            />
          </Field>
          <p className={styles.sub}>
            Kept with this visit on the record. Photos of patients are clinical
            records: take them on the clinic device, not a personal phone. JPEG,
            PNG or PDF up to 5 MB.
          </p>
        </>
      );
      action = {
        label: 'Attach',
        disabled: !file,
        run: () =>
          void a.attach
            .mutateAsync(file!)
            .then(() => {
              toast.show({ tone: 'success', title: 'Attached to the visit' });
              onBack();
            })
            .catch(fail),
      };
      break;
    case 'adr':
      body = (
        <>
          <Field label="Suspected medicine">
            <Input
              value={val('medicine', live?.items[0]?.medicine ?? '')}
              onChange={(x) => set('medicine')(x.target.value)}
              list="adr-medicines"
            />
          </Field>
          <datalist id="adr-medicines">
            {live?.items.map((i) => (
              <option key={i.id} value={i.medicine} />
            ))}
          </datalist>
          {chips(
            'Reaction',
            'reaction',
            [
              ['rash_or_hives', 'Rash or hives'],
              ['swelling_face_lips', 'Swelling of face or lips'],
              ['difficulty_breathing', 'Difficulty breathing'],
              ['vomiting', 'Vomiting'],
              ['dizziness_fainting', 'Dizziness or fainting'],
              ['other', 'Other'],
            ],
            'rash_or_hives',
          )}
          {chips(
            'Severity',
            'severity',
            [
              ['mild', 'Mild'],
              ['moderate', 'Moderate'],
              ['severe', 'Severe'],
            ],
            'mild',
          )}
          <Field label="Started · optional">
            <Input
              value={val('started')}
              placeholder="30 minutes after the first dose"
              onChange={(x) => set('started')(x.target.value)}
            />
          </Field>
          <Field label="Action taken · optional">
            <textarea
              className={styles.notes}
              rows={3}
              value={val('action')}
              onChange={(x) => set('action')(x.target.value)}
            />
          </Field>
          <p className={styles.sub}>
            Saving adds the medicine to the patient’s allergy list and prepares
            the NPRA report.
          </p>
        </>
      );
      action = {
        label: 'Save and add allergy',
        disabled:
          val('medicine', live?.items[0]?.medicine ?? '').trim().length < 2,
        run: () =>
          void submit(
            'adverse-reactions',
            {
              medicine: val('medicine', live?.items[0]?.medicine ?? '').trim(),
              reaction: val('reaction', 'rash_or_hives'),
              severity: val('severity', 'mild'),
              started: val('started').trim() || undefined,
              actionTaken: val('action').trim() || undefined,
            },
            'Reaction recorded · allergy added · NPRA report prepared',
          ),
      };
      break;
    case 'child':
      body = (
        <>
          <p className={styles.blockNote}>
            If you believe a child is being abused or neglected, the Child Act
            2001 (s. 27) requires you to inform a Social Welfare Officer at
            once. This note is restricted: only doctors and the Super admin can
            open it.
          </p>
          <Field label="Child">
            <Input
              value={val('child')}
              placeholder="Name and age"
              onChange={(x) => set('child')(x.target.value)}
            />
          </Field>
          <div className={styles.section}>
            <p className="eyebrow">Concern</p>
            <ChipToggle
              label="Concern"
              options={[
                {
                  value: 'injury_not_matching_story',
                  label: 'Injury that does not match the story',
                },
                {
                  value: 'signs_of_sexual_abuse',
                  label: 'Signs of sexual abuse',
                },
                { value: 'neglect', label: 'Neglect' },
                { value: 'emotional_abuse', label: 'Emotional abuse' },
                { value: 'child_disclosed', label: 'The child told me' },
              ]}
              selected={multi}
              onChange={setMulti}
            />
          </div>
          <Field label="What you saw and heard (exact words)">
            <textarea
              className={styles.notes}
              rows={4}
              value={val('seen')}
              onChange={(x) => set('seen')(x.target.value)}
            />
          </Field>
          <Field label="Who came with the child · optional">
            <Input
              value={val('who')}
              onChange={(x) => set('who')(x.target.value)}
            />
          </Field>
          {chips(
            'Social Welfare Officer informed by',
            'welfare',
            [
              ['not_yet', 'Not yet'],
              ['phone_jkm', 'Phone to JKM'],
              ['talian_kasih', 'Talian Kasih 15999'],
              ['in_person', 'In person'],
            ],
            'not_yet',
          )}
          <Field label="Officer name and time · optional">
            <Input
              value={val('officer')}
              onChange={(x) => set('officer')(x.target.value)}
            />
          </Field>
        </>
      );
      action = {
        label: 'Save restricted note',
        disabled:
          val('child').trim().length < 2 ||
          !multi.length ||
          val('seen').trim().length < 2,
        run: () =>
          void submit(
            'child-protection',
            {
              child: val('child').trim(),
              concerns: multi,
              whatSeen: val('seen').trim(),
              whoCame: val('who').trim() || undefined,
              welfareInformedBy: val('welfare', 'not_yet'),
              officer: val('officer').trim() || undefined,
            },
            'Restricted note saved',
          ),
      };
      break;
    case 'dv':
      body = (
        <>
          <p className={styles.blockNote}>
            Offer help without pressure. The One Stop Crisis Centre (OSCC) at
            the hospital emergency department sees survivors any time. Talian
            Kasih: 15999. In danger now: 999.
          </p>
          {chips(
            'Referral',
            'referral',
            [
              ['oscc_accepted', 'Patient accepted OSCC referral'],
              ['declined', 'Patient declined for now'],
              ['police_report', 'Police report made'],
            ],
            'oscc_accepted',
          )}
          {chips(
            'Safe contact',
            'safe',
            [
              ['safe_only', 'Do not send messages; call only this number'],
              ['normal', 'Normal contact'],
            ],
            'safe_only',
          )}
          <Field label="Safe number (if any) · optional">
            <Input
              value={val('number')}
              onChange={(x) => set('number')(x.target.value)}
            />
          </Field>
          <Field label="Note (restricted)">
            <textarea
              className={styles.notes}
              rows={3}
              value={val('note')}
              onChange={(x) => set('note')(x.target.value)}
            />
          </Field>
          <p className={styles.sub}>
            A safe-contact setting stops all app messages and texts to this
            patient, so nothing reaches a shared phone.
          </p>
        </>
      );
      action = {
        label: 'Save',
        disabled: val('note').trim().length < 2,
        run: () =>
          void submit(
            'domestic-violence',
            {
              referral: val('referral', 'oscc_accepted'),
              safeContact: val('safe', 'safe_only'),
              safeNumber: val('number').trim() || undefined,
              note: val('note').trim(),
            },
            'Saved · safe contact set',
          ),
      };
      break;
    case 'emergency': {
      const done = e.records.filter((r) => r.kind === 'emergency_step');
      body = (
        <>
          <p className={styles.blockNote}>
            Tap each step as it happens; each tap is time-stamped on the record.
          </p>
          <div className={styles.emergencyGrid}>
            {(opts?.emergencySteps ?? []).map((s) => (
              <Button
                key={s}
                variant="danger"
                loading={
                  a.submit.isPending &&
                  a.submit.variables?.body !== undefined &&
                  (a.submit.variables.body as { step?: string }).step === s
                }
                onClick={() =>
                  a.submit
                    .mutateAsync({ path: 'emergency-steps', body: { step: s } })
                    .then(() =>
                      toast.show({
                        tone: 'info',
                        title: `${STEP_LABEL[s] ?? s} · ${klTime(new Date().toISOString())}`,
                      }),
                    )
                    .catch(fail)
                }
              >
                {STEP_LABEL[s] ?? s}
              </Button>
            ))}
          </div>
          <ol className={styles.timeline} aria-label="Emergency timeline">
            {done.map((r) => (
              <li key={r.id}>
                <strong>{klTime(r.recordedAt)}</strong> ·{' '}
                {STEP_LABEL[String(r.data.step)] ?? String(r.data.step)}
              </li>
            ))}
          </ol>
        </>
      );
      break;
    }
  }

  return (
    <div className={styles.stack}>
      {body}
      <div className={styles.formActions}>
        <Button onClick={onBack}>Back to consultation</Button>
        {action && (
          <Button
            variant="primary"
            icon={
              kind === 'attach' ? <Upload size={16} /> : <Check size={16} />
            }
            disabled={action.disabled}
            loading={a.submit.isPending || a.attach.isPending}
            onClick={action.run}
          >
            {action.label}
          </Button>
        )}
      </div>
    </div>
  );
}
